import type { OrderPrintTicketStatus, OrderPrintingSummary, PrintTicketType } from '@/lib/orders-client';
import type { PrintUnavailableReason } from '@/types/brand';

/**
 * What the thermal printers did with an order, read off `order.printing`:
 *
 * - `none`     — never sent to a printer, or the branch has none (`jobsQueued === 0`): only the
 *                browser print applies.
 * - `pending`  — queued here and not answered yet, or sent and some ticket type it queued has not
 *                reported back ("Imprimiendo…").
 * - `unsent`   — the last request never reached a printer queue: munchies-api refused it or was
 *                unreachable ("No se pudo enviar a la impresora").
 * - `failed`   — at least one ticket failed or expired ("No impreso", with the printer and why).
 * - `printed`  — every ticket type the request queued printed.
 */
export type PrintStatusKind = 'none' | 'pending' | 'unsent' | 'printed' | 'failed';

/** One ticket type's outcome, as the full chip lists it. */
export interface TicketPrintStatus {
  ticketType: PrintTicketType;
  kind: 'pending' | 'printed' | 'failed';
  /** Absent while pending. */
  outcome?: OrderPrintTicketStatus;
}

export interface PrintStatus {
  kind: PrintStatusKind;
  /** The ticket types that have an outcome for the CURRENT request, comanda first. */
  tickets: TicketPrintStatus[];
  /** The first failed ticket, when `kind === 'failed'` — what the compact chip names. */
  failure?: OrderPrintTicketStatus;
  /** ISO — the newest outcome, when `kind === 'printed'`. */
  printedAt?: string;
}

const TICKET_ORDER: PrintTicketType[] = ['comanda', 'pedido'];

/**
 * How much earlier than the request an outcome may be stamped and still count as its answer. The two
 * stamps come from two servers (the agent stamps the request, munchies-api the outcome), so a small
 * clock skew must not make a fresh outcome look stale and leave the chip on "Imprimiendo…" forever.
 */
const CLOCK_SKEW_TOLERANCE_MS = 10_000;

function time(iso: string | null | undefined): number {
  if (!iso) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

/**
 * An outcome is the answer to the current request unless it predates it: after a reprint, the old
 * "printed" (or the old failure the reprint is fixing) must not stand in for the new job.
 */
function isCurrent(outcome: OrderPrintTicketStatus, requestedAt: string | undefined): boolean {
  if (!requestedAt) return true;
  const at = Date.parse(outcome.at);
  const requested = Date.parse(requestedAt);
  if (Number.isNaN(at) || Number.isNaN(requested)) return true;
  return at >= requested - CLOCK_SKEW_TOLERANCE_MS;
}

/**
 * A print was queued (on acceptance or a reprint) and munchies-api has not answered it yet: the mark
 * is newer than both the last answer and the last request error. Both stamps come from the agent.
 */
function isAwaitingAnswer(printing: OrderPrintingSummary): boolean {
  const queued = time(printing.queuedAt);
  return (
    queued > Number.NEGATIVE_INFINITY && queued > time(printing.requestedAt) && queued > time(printing.requestError?.at)
  );
}

/** The last request never reached a printer queue, and nothing has been asked or printed since. */
function hasCurrentRequestError(printing: OrderPrintingSummary): boolean {
  const error = printing.requestError;
  if (!error) return false;
  return time(error.at) >= time(printing.requestedAt);
}

/**
 * Why this order was never sent to the thermal printers, when it was deliberately skipped at
 * acceptance (`printing.skipReason`: POS connected, no printers, test order, no Menteo client). A
 * later print request removes the marker, so its presence is current by construction.
 */
export function printSkipReasonOf(order: { printing?: OrderPrintingSummary | null }): PrintUnavailableReason | null {
  return order.printing?.skipReason ?? null;
}

export function printStatusOf(order: { printing?: OrderPrintingSummary | null }): PrintStatus {
  const printing = order.printing;
  if (!printing) return { kind: 'none', tickets: [] };
  // Skipped at acceptance: nothing was asked, so there is nothing to show (no chip).
  if (printing.skipReason) return { kind: 'none', tickets: [] };

  if (isAwaitingAnswer(printing)) return { kind: 'pending', tickets: [] };
  if (hasCurrentRequestError(printing)) return { kind: 'unsent', tickets: [] };

  const tickets: TicketPrintStatus[] = [];
  for (const ticketType of TICKET_ORDER) {
    const outcome = printing[ticketType];
    if (!outcome || !isCurrent(outcome, printing.requestedAt)) continue;
    tickets.push({ ticketType, kind: outcome.status === 'printed' ? 'printed' : 'failed', outcome });
  }

  const failed = tickets.find((ticket) => ticket.kind === 'failed');
  if (failed) return { kind: 'failed', tickets, failure: failed.outcome };

  // Every ticket type the request queued must have reported before the order reads "Impreso". Older
  // rows carry no `ticketTypes`: any outcome then counts.
  const queuedTypes = (printing.jobsQueued ?? 0) > 0 ? printing.ticketTypes : undefined;
  const allReported = queuedTypes
    ? queuedTypes.every((ticketType) => tickets.some((ticket) => ticket.ticketType === ticketType))
    : tickets.length > 0;

  if (allReported && tickets.length > 0) {
    const printedAt = tickets.reduce<string | undefined>((latest, ticket) => {
      const at = ticket.outcome?.at;
      return at && (!latest || Date.parse(at) > Date.parse(latest)) ? at : latest;
    }, undefined);
    return { kind: 'printed', tickets, printedAt };
  }

  // Asked, jobs queued, not everything back yet. With no jobs the branch has no printer: nothing to wait for.
  if (printing.requestedAt && (printing.jobsQueued ?? 0) > 0) return { kind: 'pending', tickets };
  return { kind: 'none', tickets };
}

/**
 * Whether a thermal reprint is worth offering for this order: the branch printed (or tried to print)
 * it before. A branch with no printer only ever gets the browser print.
 */
export function offersThermalReprint(order: { printing?: OrderPrintingSummary | null }): boolean {
  const printing = order.printing;
  if (!printing || printing.skipReason) return false;
  return (printing.jobsQueued ?? 0) > 0 || !!printing.comanda || !!printing.pedido || hasCurrentRequestError(printing);
}

/**
 * A reprint may be ASKED for: the store accepted the order and did not cancel it since (the backend
 * refuses anything else with `ORDER_NOT_ACCEPTED`), it is not a Playground order (never printed:
 * `ORDER_PRINT_UNAVAILABLE`), no print of it is queued and still unanswered (a second one would print
 * twice), and either a printer was involved before or nothing is on record at all — the acceptance
 * print may never have reached munchies-api, and a reprint is how it gets there. A branch with no
 * printer (or no Menteo client) answers `jobsQueued: 0`, after which `offersThermalReprint` is false
 * and the button goes away on its own. An order skipped at acceptance (`skipReason`) never offers it.
 */
export function canRequestReprint(order: {
  printing?: OrderPrintingSummary | null;
  acceptedAt?: string | null;
  status: string;
  isSandbox?: boolean;
}): boolean {
  if (order.status === 'cancelled' || order.isSandbox) return false;
  // The branch cannot print (POS, no printer, no Menteo client): the backend refuses the reprint.
  if (order.printing?.skipReason) return false;
  if (order.printing && isAwaitingAnswer(order.printing)) return false;
  const accepted = !!order.acceptedAt || order.status === 'confirmed' || order.status === 'completed';
  return accepted && (order.printing == null || offersThermalReprint(order));
}
